'use client'

import clsx from 'clsx'
import { ReactNode, useCallback, useState } from 'react'
import Icon, { IconProps } from '../Icon/Icon'
import Link from '../Link/Link'

export interface NotificationProps {
  /**
   * Displays the icon element by its provided name. Also can modify its classname.
   */
  icon?: IconProps
  /**
   * Displays the title of the notification.
   */
  title: string | ReactNode
  /**
   * Displays the body of the notification.
   */
  body?: string
  /**
   * Whether the notification can be closed
   */
  dismissible?: boolean
  /**
   * Which type of notification
   * Blue for info, Pink for urgent
   */
  type?: 'info' | 'urgent' | 'edit' | 'cancel'
  /**
   * Controls the size of the content
   * `large` for bold text, `small` for regular text
   */
  size?: 'large' | 'small'
  /**
   * If provided, adds a CTA link at the bottom of the notification
   */
  link?: {
    text: string
    url: string
  }
  /**
   * Optional class name
   */
  className?: string
}

export default function Notification({
  icon,
  title,
  body,
  type = 'info',
  dismissible = false,
  size = 'large',
  link,
  className,
}: NotificationProps) {
  const [visible, setVisible] = useState(true)
  const dismiss = () => setVisible(false)

  const renderNotificationIconByType = useCallback(() => {
    switch (type) {
      case 'edit':
        return (
          // Renders only the edit icon
          <Icon
            icon="edit"
            aria-label="EditIcon"
            className={clsx('[&]:w-[22px] [&]:h-[22px] [&]:mr-0 mb-[1px]')}
          />
        )
      case 'cancel':
        return (
          // Renders only the cancel icon
          <Icon
            icon="error-circle-filled"
            aria-label="CancelIcon"
            className={clsx(
              'text-error [&]:mr-0 [&]:w-[20px] [&]:h-[20px] mb-[1px] md:mb-[3px]',
            )}
          />
        )
      default:
        if (icon) {
          return (
            // Renders icon if the icon prop is populated, otherwise renders null
            <Icon
              {...icon}
              className={clsx(
                'shrink-0 w-4 h-4 md:w-6 md:h-6 mr-2 self-start',
                icon.className,
              )}
            />
          )
        } else {
          return null
        }
    }
  }, [type, icon])

  return visible ? (
    <div
      className={clsx(
        'text-neutral-700 p-4 w-full text-center relative',
        'lg:py-4 lg:px-6',
        body && 'py-4 px-6',
        size === 'small' ? 'type-body-10' : 'type-body-7',
        type === 'urgent' || type === 'cancel'
          ? 'bg-background-red'
          : 'bg-info-blue',
        type === 'edit' || type === 'cancel' ? 'rounded-none' : 'rounded-8',
        type === 'edit' && '[&]:font-normal [&]:type-body-6',
        type === 'cancel' && '[&]:type-body-5',
        dismissible && 'pr-10',
        className,
      )}
    >
      <div
        aria-label="Notification"
        className="flex gap-2 flex-nowrap justify-center items-center"
      >
        {renderNotificationIconByType()}
        <div className="inline-flex flex-col gap-y-2 items-center justify-start">
          <p
            className={clsx(
              'text-left',
              body && 'type-body-7 lg:type-body-9 self-start',
            )}
          >
            {title}
            {body && (
              <>
                <br />
                <span className="text-left type-body-8 lg:type-body-10 !font-normal">
                  {body}
                </span>
              </>
            )}
          </p>
          {link?.text && link?.url && (
            <Link href={link.url} className="h-auto py-0 border-0" inheritFont>
              {link.text}
            </Link>
          )}
        </div>
        {dismissible ? (
          <button
            type="button"
            aria-label="dismiss notification"
            className="ml-auto cursor-pointer w-4 h-4 md:w-6 md:h-6 absolute right-4"
            onClick={dismiss}
          >
            <Icon icon="close" className="w-4 h-4 md:w-6 md:h-6" />
          </button>
        ) : null}
      </div>
    </div>
  ) : null
}
